type IntegrationPack = { connectsTo?: string[] | null };
type TooltipPack = IntegrationPack & { ui?: { isInstalled?: boolean } | null };

export const hasIntegration = (pack: IntegrationPack): boolean => (pack.connectsTo?.length ?? 0) > 0;

export const sparkTypeTooltipTextFor = (hasRealTimeMedia: boolean): string =>
	hasRealTimeMedia
		? "Live: runs while you’re on a call with the customer"
		: "On Demand: runs after a session, on its recording and photos";

// LSP-19414: category names carry a "<Group> - <Item>" convention (see useCategoryGrouping.ts);
// tags only have room for the concise item half. "Default" is its own category, not "every
// industry", so it shows as it is.
export const industryLabelFor = (categoryName?: string | null): string | null => {
	if (!categoryName) return null;
	const dashIndex = categoryName.indexOf("-");
	return dashIndex === -1 ? categoryName : categoryName.substring(dashIndex + 1).trim();
};

export const platformTooltipTextFor = (platform: string): string => `Sends results to ${platform}`;

// LSP-21629: one home for the Sparks page tooltip copy so the list rows, library cards and
// details page all say the same thing about the same control.
export const SPARK_TOOLTIPS = {
	add: "Add to My Sparks so your team can run it on sessions",
	viewDetails: "See what this Spark does, sample reports and author info",
	inMySparks: "Already in My Sparks. Your team can run it on sessions.",
	inMySparksOnDetails: "Already in My Sparks. Your team can run it on sessions.",
	edit: "Change this Spark’s name, notes or instructions",
	configure: "Set up the documents and price lists this Spark uses",
	delete: "Delete this Spark for your whole team. This can’t be undone.",
	remove: "Take it out of My Sparks. It stays in Community Sparks, so you can add it back any time.",
	removeOnDetails: "Take it out of My Sparks. You can add it back any time.",
	dragHandle: "Drag to change the order your team sees this Spark in",
	madeByTeam: "Your team created this Spark and can edit it",
	fromCommunity: "Added from Community Sparks. Only the author can edit it.",
	mySparksTab: "Sparks your team can run on sessions",
	communityTab: "Browse ready-made Sparks from Sparky AI and industry pros",
	createSpark: "Build your own Spark from scratch",
	addFromCommunity: "Find more Sparks to add in Community Sparks",
	back: "Go back without changing anything",
	clearFilters: "Reset search and filters"
} as const;

// LSP-15372: when a pack is both a Community install AND connects to an integration, the
// integration text wins - it's the more actionable disambiguator (see packBadges.spec.ts).
// Do not "fix" this to always show the community text on the globe icon; that reverts LSP-15372.
export const tooltipTextFor = (pack: TooltipPack): string => {
	if (hasIntegration(pack)) {
		return `CONNECTS TO ${pack.connectsTo!.map((platform) => platform.toUpperCase()).join(" + ")}`;
	}
	if (pack.ui?.isInstalled) {
		return "This template was created by the LiveSwitch Community";
	}
	return "";
};
